import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { useFormatInstant } from '../hooks/useClinicTimezone';
import { INSTANT_FORMATS } from '../utils/dateUtils';

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [subscriptionInfo, setSubscriptionInfo] = useState<any>(null);
  // Clinic.trialEndsAt is an INSTANT — Stripe's `trial_end` epoch x 1000
  // (DATE-KINDS.md 1.3). Rendered in the CLINIC's zone so the trial end shown on
  // this landing page is the same string Settings > Subscription shows, instead
  // of drifting a day apart for anyone not in the clinic's own zone.
  //
  // ⚠ Known gap on THIS page, deliberately left alone here. `useClinicRecord`
  // fires at mount, and at mount the clinic is usually still unpaid — the whole
  // reason this page exists is that Stripe's webhook has not landed yet.
  // `GET /clinics/current` sits behind SubscriptionGuard with no
  // `@AllowWithoutSubscription()`, so that request 403s, `retry: false` means it
  // is never retried, and the zone stays at the schema-default fallback
  // `America/New_York`. The trial end is therefore right for an Eastern clinic
  // and can read a day early for a far-western one in the evening window — the
  // same failure the browser-zone render had, but bounded to one screen instead
  // of every clinic. The follow-up is a cache fix, not a date fix: invalidate
  // CLINIC_QUERY_KEY once /subscription/status reports ACTIVE/TRIALING, so the
  // clinic row is refetched with a subscription the guard accepts. Opting the
  // endpoint out of SubscriptionGuard would also work but is a security call,
  // not a rendering one.
  const formatInstant = useFormatInstant();

  useEffect(() => {
    verifySubscription();
  }, []);

  const verifySubscription = async () => {
    try {
      const sessionId = searchParams.get('session_id');
      
      if (!sessionId) {
        setError('Invalid session. Please contact support.');
        setLoading(false);
        return;
      }

      let retries = 0;
      const maxRetries = 5;
      let subscriptionActive = false;
      
      while (retries < maxRetries && !subscriptionActive) {
        // Wait progressively longer (2s, 2s, 3s, 3s, 4s = 14s total)
        const waitTime = retries < 2 ? 2000 : retries < 4 ? 3000 : 4000;
        await new Promise(resolve => setTimeout(resolve, waitTime));
        
        const response = await api.get('/subscription/status');
        
        if (response.data.status === 'ACTIVE' || response.data.status === 'TRIALING') {
          setSubscriptionInfo(response.data);
          subscriptionActive = true;
          return;
        }
        
        retries++;
      }
      
      // If still not active after retries, try manual verification as last resort
      if (!subscriptionActive) {
        try {
          await api.post('/subscription/manual-verify', { sessionId });
          
          const statusResponse = await api.get('/subscription/status');
          
          if (statusResponse.data.status === 'ACTIVE' || statusResponse.data.status === 'TRIALING') {
            setSubscriptionInfo(statusResponse.data);
            return;
          }
        } catch (manualError) {
          console.error('❌ Manual verification failed:', manualError);
        }
        
        setError('Subscription verification taking longer than expected. Please refresh the page in a moment.');
      }
    } catch (error: any) {
      console.error('Error verifying subscription:', error);
      console.error('Error response:', error.response?.data);
      setError(`Failed to verify subscription: ${error.response?.data?.message || error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGetStarted = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8 }}>
          <Alert severity="error">{error}</Alert>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button 
              variant="contained" 
              onClick={() => {
                setError('');
                setLoading(true);
                verifySubscription();
              }}
              sx={{ mr: 2 }}
            >
              Retry Verification
            </Button>
            <Button variant="outlined" onClick={() => navigate('/paywall')}>
              Return to Pricing
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 8 }}>
        <Paper elevation={3} sx={{ p: 6, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
          
          <Typography variant="h3" component="h1" gutterBottom>
            Welcome to StableTrack!
          </Typography>
          
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            Your subscription has been activated successfully
          </Typography>

          {subscriptionInfo && (
            <Box sx={{ mb: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Subscription Plan:</strong> {subscriptionInfo.tier}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Status:</strong> {subscriptionInfo.status === 'TRIALING' ? 'Free Trial' : 'Active'}
              </Typography>
              {subscriptionInfo.trialEndsAt && (
                <Typography variant="subtitle1">
                  <strong>Trial Ends:</strong>{' '}
                  {formatInstant(subscriptionInfo.trialEndsAt, INSTANT_FORMATS.dateNumeric)}
                </Typography>
              )}
            </Box>
          )}

          <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <RocketLaunchIcon sx={{ mr: 1 }} />
            What's Next?
          </Typography>

          <List sx={{ maxWidth: 400, margin: '0 auto', textAlign: 'left', mb: 4 }}>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Add your first horse"
                secondary="Start by adding horse profiles to your clinic"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Schedule appointments"
                secondary="Manage your calendar and appointments efficiently"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Explore AI Assistant"
                secondary="Get help with clinical notes and recommendations"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Invite team members"
                secondary="Add your staff to collaborate"
              />
            </ListItem>
          </List>

          <Button
            variant="contained"
            size="large"
            onClick={handleGetStarted}
            sx={{
              bgcolor: '#C9772B',
              '&:hover': {
                bgcolor: '#6B3410',
              },
              px: 6,
              py: 1.5,
            }}
          >
            Go to Dashboard
          </Button>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
            Need help getting started?{' '}
            <Link
              component="button"
              onClick={() => window.open('mailto:support@stabletrack.ai', '_blank')}
              sx={{ textDecoration: 'none', color: '#C9772B' }}
            >
              Contact Support
            </Link>
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default SubscriptionSuccess;
